import React, { useEffect, useMemo, useState } from 'react';
import { Popover, PopoverPanel } from '@headlessui/react';
import { usePremadeAnswers as usePremadeAnswersApi } from 'flow/hooks/api/usePremadeAnswer';
import useClickOutsideHandler from 'flow/hooks/useClickOutsideHandler';
import usePremadeAnswers from 'flow/hooks/usePremadeAnswers';
import { PremadeAnswer } from 'flow/types/Chats';
import {
    insertPremadeAnswer,
    PremadeAnswerInserterState,
} from 'flow/utils/premade-answer-utils';
import { twMerge } from 'tailwind-merge';

interface Props {
    state: PremadeAnswerInserterState;
    updateState: (update: PremadeAnswerInserterState) => void;
    updateMessage: (update: string, cursorPosition: number) => void;
}

// Component is a popover showing the premade answers we could potentially insert.
export const PremadeAnswerInserter = ({
    state,
    updateState,
    updateMessage,
}: Props) => {
    const { data: allPremadeAnswers } = usePremadeAnswersApi();
    const { substitutePlaceholders } = usePremadeAnswers();

    // Apply any filtering.
    const filteredPremadeAnswers = useMemo(
        () =>
            allPremadeAnswers
                ?.filter(
                    (answer) =>
                        state.open &&
                        (!answer.type || answer.type === 'PREMADE_ANSWER') &&
                        answer.name
                            .toLowerCase()
                            .startsWith(state.filter.toLowerCase()),
                )
                ?.sort((a, b) => (a.name < b.name ? -1 : 1)),
        [allPremadeAnswers, state.open, state.open ? state.filter : ''],
    );

    // Use maximally 60% of the viewport height for answers.
    const maxAnswers = Math.ceil((window.innerHeight * 0.6) / 60);

    // The displayed answers (accounting for arrow key handling), as well as the current offset
    // of the answers we show (to give nicer UX when we use arrow keys by minimizing the amount
    // of scrolling we do).
    const [displayedPremadeAnswers, setDisplayedPremadeAnswers] =
        useState<PremadeAnswer[]>();
    const [offsetStart, setOffsetStart] = useState(0);
    useEffect(() => {
        // If no answers, nothing to show.
        if (!state.open || filteredPremadeAnswers.length === 0) {
            setDisplayedPremadeAnswers(undefined);
            setOffsetStart(0);
            return;
        }

        // Make sure insertion offset is in range; reset to 0 if not (or if unset), and determine
        // correct insertion candidate. Update state if needed.
        const newInsertionOffset = Math.min(
            Math.max(state.insertionOffset || 0, 0),
            filteredPremadeAnswers.length - 1,
        );
        const newProposedInsertion = filteredPremadeAnswers[newInsertionOffset];
        if (
            state.insertionOffset !== newInsertionOffset ||
            state.proposedInsertion?.key !== newProposedInsertion.key
        ) {
            updateState({
                ...state,
                insertionOffset: newInsertionOffset,
                proposedInsertion: newProposedInsertion,
            });
        }

        // Make sure the offset start doesn't result in us showing less than the
        // desired number of mentions, and that the mention we are currently on
        // is in the window.
        let newOffsetStart = offsetStart;
        if (filteredPremadeAnswers.length - offsetStart < maxAnswers) {
            newOffsetStart = 0;
        }
        if (newInsertionOffset < offsetStart) {
            // We're too far down the list; move up.
            newOffsetStart = newInsertionOffset;
        } else if (newInsertionOffset >= offsetStart + maxAnswers) {
            // We're too far up the list; move down.
            newOffsetStart = newInsertionOffset - maxAnswers + 1;
        }
        setDisplayedPremadeAnswers(
            filteredPremadeAnswers.slice(
                newOffsetStart,
                newOffsetStart + maxAnswers,
            ),
        );
        if (newOffsetStart !== offsetStart) {
            setOffsetStart(newOffsetStart);
        }
    }, [
        filteredPremadeAnswers?.length,
        state.open ? state.insertionOffset : -1,
        offsetStart,
    ]);

    const popoverRef = useClickOutsideHandler(() =>
        updateState({ open: false }),
    );

    return (
        <Popover>
            {state.open && displayedPremadeAnswers && (
                <PopoverPanel
                    static={true}
                    className="absolute start-3 top-0 z-40 w-full -translate-y-full pe-8 outline-hidden"
                    ref={popoverRef}
                >
                    <div className="w-full rounded-sm bg-white p-2 shadow-lg">
                        {displayedPremadeAnswers.map((answer, index) => (
                            <div
                                key={answer.key}
                                className={twMerge(
                                    'flex cursor-pointer flex-col p-2',
                                    state.open &&
                                        state.proposedInsertion?.key ===
                                            answer.key
                                        ? 'bg-gray-200'
                                        : 'hover:bg-gray-100',
                                )}
                                onMouseEnter={() =>
                                    updateState({
                                        ...state,
                                        insertionOffset: offsetStart + index,
                                    })
                                }
                                onWheel={(e) => {
                                    const notches =
                                        Math.ceil(Math.abs(e.deltaY / 120)) *
                                        (e.deltaY < 0 ? -1 : 1);
                                    const maxOffsetStart = Math.max(
                                        filteredPremadeAnswers.length -
                                            maxAnswers,
                                        0,
                                    );
                                    setOffsetStart(
                                        Math.min(
                                            Math.max(offsetStart + notches, 0),
                                            maxOffsetStart,
                                        ),
                                    );
                                }}
                                onClick={() =>
                                    insertPremadeAnswer(
                                        substitutePlaceholders(answer.message),
                                        updateState,
                                        updateMessage,
                                    )
                                }
                            >
                                <p className="text-sm/5 text-gray-800">
                                    {answer.name}
                                </p>
                                <p className="truncate overflow-hidden text-xs/4 whitespace-nowrap text-gray-500">
                                    {substitutePlaceholders(answer.message)}
                                </p>
                            </div>
                        ))}
                    </div>
                </PopoverPanel>
            )}
        </Popover>
    );
};
